import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, FileText, Mic, X, Download, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../services/api';

const BAR_COUNT = 28;
const waveformCache = {};

// A URL assinada do S3 vale 3600s. Cachear por 45min evita refazer o GET a
// cada remontagem do MessageList (rolar a lista remonta as mídias). Guarda a
// promise, não a URL, para dois itens do mesmo álbum não dispararem 2 requests.
const TTL_URL_MIDIA = 45 * 60 * 1000;
const urlMidiaCache = new Map();

const obterUrlMidia = (id) => {
  const agora = Date.now();
  const cacheado = urlMidiaCache.get(id);
  if (cacheado && agora - cacheado.em < TTL_URL_MIDIA) return cacheado.promise;

  const promise = api.get(`/conversas/media/${id}`).then(r => r.data.url);
  // Falha não fica cacheada: senão um erro transitório vira erro permanente.
  promise.catch(() => urlMidiaCache.delete(id));
  urlMidiaCache.set(id, { em: agora, promise });
  return promise;
};

const analyzeAudio = async (url) => {
  if (waveformCache[url]) return waveformCache[url];
  try {
    const res = await fetch(url);
    const buf = await res.arrayBuffer();
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const audio = await ctx.decodeAudioData(buf);
    const raw = audio.getChannelData(0);
    const step = Math.floor(raw.length / BAR_COUNT);
    const peaks = [];
    for (let i = 0; i < BAR_COUNT; i++) {
      let sum = 0;
      for (let j = 0; j < step; j++) sum += Math.abs(raw[i * step + j]);
      peaks.push(sum / step);
    }
    const max = Math.max(...peaks) || 1;
    const result = peaks.map(p => 0.1 + (p / max) * 0.9);
    waveformCache[url] = result;
    ctx.close();
    return result;
  } catch (e) {
    console.warn('Waveform analysis failed:', e);
    return null;
  }
};

// Fallback determinístico por ID
const fakeWaveform = (seed) => {
  let h = 5381;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) + h + seed.charCodeAt(i)) & 0xffffffff;
  return Array.from({ length: BAR_COUNT }, (_, i) => {
    h = ((h * 1103515245) + 12345) & 0x7fffffff;
    const v = (h % 1000) / 1000;
    const env = 0.4 + 0.6 * Math.sin((i / BAR_COUNT) * Math.PI);
    return 0.15 + v * 0.85 * env;
  });
};

const fmt = (s) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;

const AudioPlayer = ({ url, darkMode, isOutgoing, timestamp, conversaId }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [bars, setBars] = useState(waveformCache[url] || fakeWaveform(conversaId || 'x'));

  useEffect(() => {
    if (url) analyzeAudio(url).then(result => { if (result) setBars(result); });
  }, [url]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => { setCurrentTime(a.currentTime); setProgress(a.duration ? (a.currentTime / a.duration) * 100 : 0); };
    const onMeta = () => setDuration(a.duration);
    const onEnd = () => { setPlaying(false); setProgress(0); setCurrentTime(0); };
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('ended', onEnd);
    return () => { a.removeEventListener('timeupdate', onTime); a.removeEventListener('loadedmetadata', onMeta); a.removeEventListener('ended', onEnd); };
  }, []);

  const toggle = () => {
    if (playing) audioRef.current.pause();
    else audioRef.current.play();
    setPlaying(!playing);
  };

  const seek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    if (audioRef.current.duration) audioRef.current.currentTime = pct * audioRef.current.duration;
  };

  const playedC = isOutgoing ? 'rgba(255,255,255,0.85)' : '#009a7a';
  const unplayedC = isOutgoing ? 'rgba(255,255,255,0.3)' : darkMode ? 'rgba(255,255,255,0.2)' : '#b5b5b5';
  const timeCls = isOutgoing ? 'text-white/60' : darkMode ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className="flex items-center gap-3" style={{ minWidth: 220 }}>
      <audio ref={audioRef} src={url} preload="metadata" />
      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: isOutgoing ? 'rgba(255,255,255,0.15)' : darkMode ? '#374151' : '#d1d5db' }}>
        <Mic size={18} style={{ color: isOutgoing ? '#fff' : darkMode ? '#9ca3af' : '#6b7280' }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <button onClick={toggle} className="flex-shrink-0"
            style={{ color: isOutgoing ? '#fff' : darkMode ? '#e5e7eb' : '#374151' }}>
            {playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
          </button>
          <div className="flex-1 flex items-center gap-px cursor-pointer" style={{ height: 26 }} onClick={seek}>
            {bars.map((h, i) => (
              <div key={i} style={{
                flex: 1,
                height: `${h * 100}%`,
                borderRadius: 2,
                background: (i / BAR_COUNT) * 100 < progress ? playedC : unplayedC,
                transition: 'background 0.15s',
              }} />
            ))}
          </div>
        </div>
        <div className="flex justify-between" style={{ paddingLeft: 26, marginTop: 2 }}>
          <span className={timeCls} style={{ fontSize: 11 }}>
            {playing ? fmt(currentTime) : duration ? fmt(duration) : '0:00'}
          </span>
          {timestamp && <span className={timeCls} style={{ fontSize: 11 }}>{timestamp}</span>}
        </div>
      </div>
    </div>
  );
};

const MediaViewer = ({ url, type, onClose, conversaId }) => {
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') setZoom(z => Math.min(z + 0.25, 4));
      if (e.key === '-') setZoom(z => Math.max(z - 0.25, 0.5));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleDownload = () => {
    const apiBase = process.env.REACT_APP_API_URL || 'https://api.nomoz.tech';
    const token = localStorage.getItem('token');
    fetch(`${apiBase}/conversas/media/${conversaId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.blob()).then(b => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = conversaId;
      a.click();
      URL.revokeObjectURL(a.href);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.9)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-end gap-2 p-3 z-10">
        {type === 'image' && (
          <>
            <button onClick={() => setZoom(z => Math.min(z + 0.25, 4))} className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"><ZoomIn size={20} /></button>
            <button onClick={() => setZoom(z => Math.max(z - 0.25, 0.5))} className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"><ZoomOut size={20} /></button>
          </>
        )}
        <button onClick={handleDownload} className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"><Download size={20} /></button>
        <button onClick={onClose} className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"><X size={20} /></button>
      </div>

      {/* Content */}
      {type === 'image' ? (
        <img src={url} alt="" className="max-h-[90vh] max-w-[90vw] object-contain transition-transform"
          style={{ transform: `scale(${zoom})` }} draggable={false} />
      ) : (
        <video src={url} controls autoPlay className="max-h-[90vh] max-w-[90vw]" />
      )}
    </div>
  );
};

const AlbumCarousel = ({ items, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const current = items[currentIndex];

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') setCurrentIndex(index => (index - 1 + items.length) % items.length);
      if (event.key === 'ArrowRight') setCurrentIndex(index => (index + 1) % items.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [items.length, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95" onClick={event => event.target === event.currentTarget && onClose()}>
      <button onClick={onClose} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 text-white hover:bg-white/20" aria-label="Fechar galeria">
        <X size={24} />
      </button>
      <div className="absolute top-5 left-1/2 -translate-x-1/2 text-white text-sm font-medium">
        {currentIndex + 1} / {items.length}
      </div>
      {items.length > 1 && (
        <>
          <button onClick={() => setCurrentIndex(index => (index - 1 + items.length) % items.length)} className="absolute left-2 sm:left-6 p-2 rounded-full bg-white/10 text-white hover:bg-white/20" aria-label="Mídia anterior">
            <ChevronLeft size={30} />
          </button>
          <button onClick={() => setCurrentIndex(index => (index + 1) % items.length)} className="absolute right-2 sm:right-6 p-2 rounded-full bg-white/10 text-white hover:bg-white/20" aria-label="Próxima mídia">
            <ChevronRight size={30} />
          </button>
        </>
      )}
      {current?.url ? (
        current.type === 'video' ? (
          <video key={current.id} src={current.url} controls autoPlay className="max-w-[88vw] max-h-[88vh] object-contain" />
        ) : (
          <img src={current.url} alt="" className="max-w-[88vw] max-h-[88vh] object-contain" />
        )
      ) : (
        <div className="w-64 h-64 rounded-lg bg-white/10 animate-pulse" />
      )}
    </div>
  );
};

export const MediaAlbum = ({ messages, darkMode }) => {
  const albumKey = messages.map(message => message.id).join(',');
  const [items, setItems] = useState(() => messages.map(message => ({ id: message.id, type: message.message_type, url: null })));
  const [carouselIndex, setCarouselIndex] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setItems(messages.map(message => ({ id: message.id, type: message.message_type, url: null })));
    Promise.all(messages.map(async message => {
      try {
        const url = await obterUrlMidia(message.id);
        return { id: message.id, type: message.message_type, url };
      } catch (_) {
        return { id: message.id, type: message.message_type, url: null };
      }
    })).then(result => { if (!cancelled) setItems(result); });
    return () => { cancelled = true; };
  // albumKey identifica precisamente quando a composição do álbum mudou.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [albumKey]);

  const visibleItems = items.slice(0, 4);
  const total = items.length;

  return (
    <>
      <div
        className="grid grid-cols-2 grid-rows-2 gap-0.5 rounded-lg overflow-hidden bg-gray-200 dark:bg-zinc-700"
        style={{ width: 280, aspectRatio: '1 / 1', maxWidth: '100%' }}
        aria-label={`Álbum com ${total} mídias`}
      >
        {visibleItems.map((item, index) => {
          const spanClass = total === 2 ? 'row-span-2' : total === 3 && index === 0 ? 'row-span-2' : '';
          const remaining = index === 3 && total > 4 ? total - 4 : 0;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setCarouselIndex(index)}
              className={`relative min-w-0 min-h-0 overflow-hidden bg-gray-300 dark:bg-zinc-600 ${spanClass}`}
              aria-label={`Abrir mídia ${index + 1} de ${total}`}
            >
              {item.url ? (
                item.type === 'video' ? (
                  <video src={item.url} muted preload="metadata" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <img src={item.url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                )
              ) : (
                <span className="absolute inset-0 animate-pulse bg-black/10 dark:bg-white/10" />
              )}
              {item.type === 'video' && !remaining && (
                <span className="absolute inset-0 flex items-center justify-center text-white drop-shadow-lg">
                  <Play size={30} fill="currentColor" />
                </span>
              )}
              {remaining > 0 && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/55 text-white text-3xl font-medium">
                  +{remaining}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {carouselIndex !== null && (
        <AlbumCarousel items={items} initialIndex={carouselIndex} onClose={() => setCarouselIndex(null)} />
      )}
    </>
  );
};

const getMediaBoxStyle = (messageType, width, height, isSharedMedia = false) => {
  if (isSharedMedia) return { width: 280, height: 340, maxWidth: '100%' };
  if (messageType === 'audio') return { width: 280, height: 56, maxWidth: '100%' };
  if (messageType === 'document') return { width: 280, height: 64, maxWidth: '100%' };

  const sourceWidth = Number(width);
  const sourceHeight = Number(height);
  if (sourceWidth > 0 && sourceHeight > 0) {
    const scale = Math.min(280 / sourceWidth, 320 / sourceHeight);
    return {
      width: Math.round(sourceWidth * scale),
      aspectRatio: `${sourceWidth} / ${sourceHeight}`,
      maxWidth: '100%',
    };
  }

  return { width: 240, aspectRatio: '4 / 3', maxWidth: '100%' };
};

const MediaMessage = ({ conversaId, messageType, darkMode, isOutgoing, timestamp, displayText, mediaWidth, mediaHeight }) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [viewer, setViewer] = useState(false);
  const [error, setError] = useState(false);
  const isSharedMedia = /^\[(Post|Reel|Story)\]/.test(displayText || '') && (messageType === 'image' || messageType === 'video');
  const mediaBoxStyle = getMediaBoxStyle(messageType, mediaWidth, mediaHeight, isSharedMedia);

  useEffect(() => {
    setMediaLoaded(false);
    setError(false);
    obterUrlMidia(conversaId).then(u => {
      setUrl(u);
    }).catch(() => { setError(true); }).finally(() => setLoading(false));
  }, [conversaId]);

  const Spinner = () => !mediaLoaded ? (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-400 border-t-transparent" />
    </div>
  ) : null;

  if (loading) return (
    <div
      style={mediaBoxStyle}
      className="rounded-lg bg-gray-200 dark:bg-zinc-700 animate-pulse flex-shrink-0"
      aria-label="Carregando mídia"
    />
  );
  if (!url || error) {
    const labels = { audio: '🎵 Áudio indisponível', video: '🎬 Vídeo indisponível', image: '🖼️ Imagem indisponível', document: '📄 Documento indisponível' };
    return (
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
        isOutgoing ? 'bg-white/10 text-white/60' : darkMode ? 'bg-zinc-600 text-gray-400' : 'bg-gray-100 text-gray-500'
      }`} style={{ maxWidth: 280 }}>
        <span>{labels[messageType] || '📎 Mídia indisponível'}</span>
      </div>
    );
  }

  // Post/Reel/Story compartilhado — card estilo Instagram
  const shareMatch = displayText?.match(/^\[(Post|Reel|Story)\]\s*([\s\S]*)/);
  if (shareMatch && (messageType === 'image' || messageType === 'video')) {
    const shareType = shareMatch[1];
    const rawCaption = shareMatch[2]?.trim() || '';
    const linkMatch = rawCaption.match(/(https?:\/\/\S+)/);
    const igLink = linkMatch ? linkMatch[1] : null;
    const caption = rawCaption.replace(/(https?:\/\/\S+)/, '').trim();
    const icons = { Post: '📷', Reel: '🎬', Story: '📸' };
    const isVideo = messageType === 'video';

    const handleClick = () => setViewer(true);

    return (
      <>
        <div className={`rounded-xl overflow-hidden mb-1 cursor-pointer border ${
          isOutgoing ? 'border-white/20' : darkMode ? 'border-zinc-600' : 'border-gray-200'
        }`} style={{ width: 280, maxWidth: '100%', minHeight: 340 }} onClick={handleClick}>
          {isVideo ? (
            <div className="w-full bg-gray-200 dark:bg-zinc-600 relative" style={{ height: 280 }}>
              <Spinner />
              <video src={url} className="w-full h-full object-cover" muted preload="metadata" onLoadedData={() => setMediaLoaded(true)} />
            </div>
          ) : (
            <div className="w-full bg-gray-200 dark:bg-zinc-600 relative" style={{ height: 280 }}>
              <Spinner />
              <img src={url} alt="" className="w-full h-full object-cover" onLoad={() => setMediaLoaded(true)} />
            </div>
          )}
          <div className={`px-3 py-2 ${isOutgoing ? 'bg-white/10' : darkMode ? 'bg-zinc-700' : 'bg-gray-50'}`}>
            <div className={`text-[11px] font-semibold mb-0.5 flex items-center gap-1 ${isOutgoing ? 'text-white/70' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {icons[shareType]} {shareType} do Instagram
            </div>
            {caption && (
              <p className={`text-xs leading-snug line-clamp-3 ${isOutgoing ? 'text-white/90' : darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                {caption}
              </p>
            )}
            {igLink && (
              <a href={igLink} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-500 hover:underline mt-1 block" onClick={e => e.stopPropagation()}>
                Ver no Instagram ↗
              </a>
            )}
          </div>
        </div>
        {viewer && <MediaViewer url={url} type={isVideo ? 'video' : 'image'} onClose={() => setViewer(false)} conversaId={conversaId} />}
      </>
    );
  }

  if (messageType === 'image') {
    return (
      <>
        <div className="rounded-lg mb-1 overflow-hidden cursor-pointer bg-gray-200 dark:bg-zinc-700 relative flex-shrink-0" style={mediaBoxStyle} onClick={() => setViewer(true)}>
          <Spinner />
          <img src={url} alt="" className="absolute inset-0 w-full h-full object-cover" onLoad={() => setMediaLoaded(true)} />
        </div>
        {viewer && <MediaViewer url={url} type="image" onClose={() => setViewer(false)} conversaId={conversaId} />}
      </>
    );
  }
  if (messageType === 'audio') {
    const transcricao = displayText?.replace(/^\[Áudio\]\s*/, '').trim();
    return (
      <div style={{ width: 280, maxWidth: '100%', minHeight: 56 }}>
        <AudioPlayer url={url} darkMode={darkMode} isOutgoing={isOutgoing} timestamp={timestamp} conversaId={conversaId} />
        {transcricao && transcricao !== '[Áudio]' && (
          <p className={`text-xs mt-1 italic break-words ${isOutgoing ? 'text-white/70' : darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {transcricao}
          </p>
        )}
      </div>
    );
  }
  if (messageType === 'video') {
    return (
      <>
        <div className="rounded-lg mb-1 overflow-hidden cursor-pointer bg-gray-200 dark:bg-zinc-700 relative flex-shrink-0" style={mediaBoxStyle} onClick={() => setViewer(true)}>
          <Spinner />
          <video src={url} className="absolute inset-0 w-full h-full object-cover" preload="metadata" onLoadedData={() => setMediaLoaded(true)} />
        </div>
        {viewer && <MediaViewer url={url} type="video" onClose={() => setViewer(false)} conversaId={conversaId} />}
      </>
    );
  }
  const handleDocDownload = () => {
    const apiBase = process.env.REACT_APP_API_URL || 'https://api.nomoz.tech';
    const token = localStorage.getItem('token');
    fetch(`${apiBase}/conversas/media/${conversaId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.blob()).then(b => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = docName;
      a.click();
      URL.revokeObjectURL(a.href);
    });
  };

  // Extract filename from displayText like "[Documento] relatorio.pdf"
  const docName = displayText?.replace(/^\[(Imagem|Áudio|Vídeo|Documento)\]\s*/, '') || 'documento';
  const docExt = docName.split('.').pop()?.toUpperCase() || '';

  return (
    <div onClick={handleDocDownload}
      style={{ width: 280, maxWidth: '100%', minHeight: 64 }}
      className={`flex items-center gap-3 p-3 rounded-lg mb-1 cursor-pointer transition-colors ${
        isOutgoing ? 'bg-white/10 hover:bg-white/20' : darkMode ? 'bg-zinc-600 hover:bg-zinc-500' : 'bg-gray-100 hover:bg-gray-200'
      }`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
        isOutgoing ? 'bg-white/20' : darkMode ? 'bg-zinc-500' : 'bg-red-100'
      }`}>
        <FileText size={20} className={isOutgoing ? 'text-white' : darkMode ? 'text-gray-300' : 'text-red-500'} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm truncate ${isOutgoing ? 'text-white' : darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{docName}</p>
        {docExt && <p className={`text-[10px] ${isOutgoing ? 'text-white/50' : 'opacity-50'}`}>{docExt}</p>}
      </div>
      <Download size={18} className={`flex-shrink-0 ${isOutgoing ? 'text-white/60' : 'opacity-40'}`} />
    </div>
  );
};

export default MediaMessage;
